export type SubnavItem = { href: string; label: string };

/**
 * sectionTitle — заголовок раздела над подменю (хлебные крошки: «Главная / … / страница»).
 * homeHref / sectionHref — «Главная» и корень раздела (например «О центре», «Образование»).
 */
export type SubnavConfig = {
  items: SubnavItem[];
  activeHref: string;
  sectionTitle: string;
  homeHref: string;
  sectionHref: string;
  /** Крошки: «Главная / {sectionTitle}» без третьего уровня */
  simpleBreadcrumb?: boolean;
};

/** develop: `npm run dev` (в бандле NODE_ENV=development) */
const ITEMS_DEV: SubnavItem[] = [
  { href: 'about.html', label: 'Общее' },
  { href: 'about-construction.html', label: 'Этапы строительства' },
  { href: 'about-team.html', label: 'Команда' },
  { href: 'about-documents.html', label: 'Документы и отчетность' },
  { href: 'about-fund.html', label: 'О фонде' },
];

/** production: PHP, URL — правь пути при необходимости */
const ITEMS_PROD: SubnavItem[] = [
  { href: '/about', label: 'Общее' },
  { href: '/about/construction/', label: 'Этапы строительства' },
  { href: '/about/team/', label: 'Команда' },
  { href: '/about/documents/', label: 'Документы и отчетность' },
  { href: '/about/fund/', label: 'О фонде' },
];

const ABOUT_SECTION_TITLE = 'О центре';

const HOME_DEV = 'index.html';
const SECTION_ROOT_DEV = 'about.html';
const HOME_PROD = '/';
const SECTION_ROOT_PROD = '/about';

function aboutEntryDev(activeHref: string): SubnavConfig {
  return {
    items: ITEMS_DEV,
    activeHref,
    sectionTitle: ABOUT_SECTION_TITLE,
    homeHref: HOME_DEV,
    sectionHref: SECTION_ROOT_DEV,
  };
}

function aboutEntryProd(activeHref: string): SubnavConfig {
  return {
    items: ITEMS_PROD,
    activeHref,
    sectionTitle: ABOUT_SECTION_TITLE,
    homeHref: HOME_PROD,
    sectionHref: SECTION_ROOT_PROD,
  };
}

export const subnavAboutByNamespaceDev: Record<string, SubnavConfig> = {
  about: aboutEntryDev('about.html'),
  'about-construction': aboutEntryDev('about-construction.html'),
  'about-team': aboutEntryDev('about-team.html'),
  'about-documents': aboutEntryDev('about-documents.html'),
  'about-faq': aboutEntryDev('about-faq.html'),
  'about-fund': aboutEntryDev('about-fund.html'),
};

export const subnavAboutByNamespaceProd: Record<string, SubnavConfig> = {
  about: aboutEntryProd('/about'),
  'about-construction': aboutEntryProd('/about/construction/'),
  'about-team': aboutEntryProd('/about/team/'),
  'about-documents': aboutEntryProd('/about/documents/'),
  'about-faq': aboutEntryProd('/about/faq/'),
  'about-fund': aboutEntryProd('/about/fund/'),
};

const EDUCATION_SECTION_TITLE = 'Образование';

const EDUCATION_ITEMS_DEV: SubnavItem[] = [
  { href: 'education-management.html', label: 'Об Управлении образования' },
  { href: 'education-programs.html', label: 'Образовательные программы' },
];

const EDUCATION_ITEMS_PROD: SubnavItem[] = [
  { href: '/education', label: 'Управление образования' },
  { href: '/education/programs/', label: 'Программы обучения' },
  { href: '/education/permission/', label: 'Разрешение на образование' },
];

const EDUCATION_SECTION_ROOT_DEV = 'education-management.html';
const EDUCATION_SECTION_ROOT_PROD = '/education';

function educationEntryDev(activeHref: string): SubnavConfig {
  return {
    items: EDUCATION_ITEMS_DEV,
    activeHref,
    sectionTitle: EDUCATION_SECTION_TITLE,
    homeHref: HOME_DEV,
    sectionHref: EDUCATION_SECTION_ROOT_DEV,
  };
}

function educationEntryProd(activeHref: string): SubnavConfig {
  return {
    items: EDUCATION_ITEMS_PROD,
    activeHref,
    sectionTitle: EDUCATION_SECTION_TITLE,
    homeHref: HOME_PROD,
    sectionHref: EDUCATION_SECTION_ROOT_PROD,
  };
}

export const subnavEducationByNamespaceDev: Record<string, SubnavConfig> = {
  'education-management': educationEntryDev('education-management.html'),
  'education-programs': educationEntryDev('education-programs.html'),
  'education-permission': educationEntryDev('education-permission.html'),
};

export const subnavEducationByNamespaceProd: Record<string, SubnavConfig> = {
  'education-management': educationEntryProd('/education'),
  'education-programs': educationEntryProd('/education/programs/'),
  'education-permission': educationEntryProd('/education/permission/'),
};

const RESIDENCY_SECTION_TITLE = 'Резидентство';

const RESIDENCY_ITEMS_DEV: SubnavItem[] = [
  { href: 'residency.html', label: 'Условия и преимущества' },
  { href: 'residency.html?tab=residents', label: 'Наши резиденты' },
];

const RESIDENCY_ITEMS_PROD: SubnavItem[] = [
  { href: '/residency', label: 'Условия и преимущества' },
  { href: '/residency?tab=residents', label: 'Наши резиденты' },
];

const RESIDENCY_SECTION_ROOT_DEV = 'residency.html';
const RESIDENCY_SECTION_ROOT_PROD = '/residency';

function residencyEntryDev(activeHref: string): SubnavConfig {
  return {
    items: RESIDENCY_ITEMS_DEV,
    activeHref,
    sectionTitle: RESIDENCY_SECTION_TITLE,
    homeHref: HOME_DEV,
    sectionHref: RESIDENCY_SECTION_ROOT_DEV,
  };
}

function residencyEntryProd(activeHref: string): SubnavConfig {
  return {
    items: RESIDENCY_ITEMS_PROD,
    activeHref,
    sectionTitle: RESIDENCY_SECTION_TITLE,
    homeHref: HOME_PROD,
    sectionHref: RESIDENCY_SECTION_ROOT_PROD,
  };
}

export const subnavResidencyByNamespaceDev: Record<string, SubnavConfig> = {
  'residency-become': residencyEntryDev('residency-become.html'),
};

export const subnavResidencyByNamespaceProd: Record<string, SubnavConfig> = {
  'residency-become': residencyEntryProd('/residency/become/'),
};

const NEWS_SECTION_TITLE = 'Новости и мероприятия';

const NEWS_ITEMS_DEV: SubnavItem[] = [
  { href: 'news-feed.html', label: 'Новости' },
  { href: 'news-events-calendar.html', label: 'Мероприятия' },
];

const NEWS_ITEMS_PROD: SubnavItem[] = [
  { href: '/news', label: 'Новости' },
  { href: '/news/calendar/', label: 'Мероприятия' },
];

const NEWS_SECTION_ROOT_DEV = 'news-feed.html';
const NEWS_SECTION_ROOT_PROD = '/news';

function newsEntryDev(activeHref: string): SubnavConfig {
  return {
    items: NEWS_ITEMS_DEV,
    activeHref,
    sectionTitle: NEWS_SECTION_TITLE,
    homeHref: HOME_DEV,
    sectionHref: NEWS_SECTION_ROOT_DEV,
    simpleBreadcrumb: true,
  };
}

function newsEntryProd(activeHref: string): SubnavConfig {
  return {
    items: NEWS_ITEMS_PROD,
    activeHref,
    sectionTitle: NEWS_SECTION_TITLE,
    homeHref: HOME_PROD,
    sectionHref: NEWS_SECTION_ROOT_PROD,
    simpleBreadcrumb: true,
  };
}

export const subnavNewsByNamespaceDev: Record<string, SubnavConfig> = {
  'news-feed': newsEntryDev('news-feed.html'),
  'news-events-calendar': newsEntryDev('news-events-calendar.html'),
};

export const subnavNewsByNamespaceProd: Record<string, SubnavConfig> = {
  'news-feed': newsEntryProd('/news'),
  'news-events-calendar': newsEntryProd('/news/calendar/'),
};

const isDevelop = process.env.NODE_ENV === 'development';

export const subnavByNamespace: Record<string, SubnavConfig> = isDevelop
  ? {
      ...subnavAboutByNamespaceDev,
      ...subnavResidencyByNamespaceDev,
      ...subnavNewsByNamespaceDev,
    }
  : {
      ...subnavAboutByNamespaceProd,
      ...subnavResidencyByNamespaceProd,
      ...subnavNewsByNamespaceProd,
    };
